import { useEffect, useState, type ReactNode } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { decoder, getColor } from '@/helpers/utils';
import { Col, Nav, Offcanvas, Row, Spinner, Tab } from 'react-bootstrap';
import { TbArrowBack } from 'react-icons/tb';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { detailTabsConfig } from '../../config/detailTabsConfig';
import { useLazyGetDetailCountQuery } from '@/app/Crawl/api/detailsApi';
import type { CrawlRequestBody } from '@/app/Crawl/api/crawlApi';
import { useSelector } from 'react-redux';
import { useAppContext } from '@/Main';
import { MdCheck, MdContentCopy, MdOutlineOpenInNew } from 'react-icons/md';
import { tabCreator } from '../../pages/tabCreator';
import useDomain from '@/hooks/useDomain';
import { isPresentQueryValue } from '@/utils/queryString';
import HtmlParser from '@/app/common/components/CellRenderers/HtmlParser';
import countSuffix from '@/helpers/countSuffix';

// جزییات صفحه
type PageDrawerState = {
  userData?: {
    data?: {
      path?: string;
    };
  };
};

type DetailTabConfig = {
  tab: string;
  title: string;
  cellRenderer: ReactNode;
};

type DetailTabsMap = Record<string, DetailTabConfig>;

type DetailCountResponse = {
  data?: Record<string, any>;
};

const DRAWER_QUERY_KEYS = [
  'details',
  'type',
  'activeTab',
  'page_url',
  'job_page',
  'status_code',
  'asset_url'
];

const PageDrawer = () => {
  const navigate = useNavigate();
  const path = useSelector(
    (state: PageDrawerState) => state.userData?.data?.path
  );
  void path;
  const [searchParams, setSearchParams] = useSearchParams();
  const { domain_slug } = useDomain();

  // گرفتن تعداد جزییات
  const [getCounts, { data: rawData, isFetching }] =
    useLazyGetDetailCountQuery();
  const data = rawData as DetailCountResponse | undefined;
  useEffect(() => {
    if (searchParams.get('details')) {
      const toApiObj: CrawlRequestBody = {
        type:
          searchParams.get('type') || searchParams.get('details') || 'internal',
        job_page: searchParams.get('job_page'),
        domain_slug
      };
      if (searchParams.get('details') === 'assets_all') {
        toApiObj['page_url'] = decoder(
          (searchParams.get('asset_url') || searchParams.get('page_url')) as string
        );
      } else if (searchParams.get('page_url')) {
        toApiObj['page_url'] = decoder(searchParams.get('page_url') as string);
      } else {
        toApiObj['page_url'] = decoder(searchParams.get('page_url') as string);
      }
      getCounts(toApiObj);
    }
  }, [
    searchParams.get('details'),
    searchParams.get('page_url'),
    searchParams.get('asset_url'),
    searchParams.get('type'),
    searchParams.get('job_page'),
    domain_slug
  ]);
  const {
    config: { theme }
  } = useAppContext();

  // مشخص کردن رنگ تب بر اساس تم دارک یا لایت در حالت اکتیو
  const tabColorFunc = (isActive: boolean) => {
    if (isActive && theme === 'dark') {
      return 'rgba(0,0,0,.2)';
    }
    if (isActive && theme === 'light') {
      return 'rgba(0,0,0,.02)';
    } else {
      return '';
    }
  };

  return (
    <Offcanvas
      onShow={() => {
        const offcanvasElement = document.querySelector('.offcanvas');
        if (offcanvasElement) {
          offcanvasElement.removeAttribute('tabindex');
        }
      }}
      show={searchParams.get('details') ? true : false}
      onHide={() => {
        const nextParams = new URLSearchParams(searchParams);
        DRAWER_QUERY_KEYS.forEach(key => nextParams.delete(key));
        if (!isPresentQueryValue(nextParams.get('issues'))) {
          nextParams.delete('issues');
        }
        setSearchParams(nextParams, { replace: true });
      }}
      style={{ backgroundColor: 'var(--falcon-body-bg)' }}
    >
      <Offcanvas.Header closeButton>
        {/* هدر بخش جزییات */}
        <Offcanvas.Title>
          <div className="d-flex flex-wrap gap-3 align-items-center">
            <div className="fs-0">اطلاعات نشانی</div>
            <div className="vr"></div>
            <div className="fs--1">
              <Url url={decoder(searchParams?.get('page_url') || '')} />
            </div>
          </div>
        </Offcanvas.Title>
        <div
          style={{ left: 40 }}
          className="fs-3 cursor-pointer position-absolute "
          onClick={() => {
            navigate(-1);
          }}
        >
          <TbArrowBack className="little-icon" fontSize="inherit" />
        </div>
        {/* <Button>بازگشت</Button> */}
      </Offcanvas.Header>
      <hr className="my-0" />
      <Offcanvas.Body className="pb-5 pt-0">
        <Tab.Container
          // مشخص کردن تب فعال در صورت وجود پیش‌فرض در پارامتر
          activeKey={searchParams.get('activeTab') || ''}
          // مشخص کردن تب فعال در صورت وجود پیش‌فرض در پارامتر
          onSelect={(eventKey: string | null) => {
            searchParams.set('activeTab', String(eventKey));
            setSearchParams(searchParams);
          }}
        >
          <Row className="p-0">
            <Col className="px-0" xs={12} md={2}>
              <Nav
                variant="tabs"
                style={{
                  borderLeft: '2px solid var(--falcon-border-color)'
                }}
                className="d-flex position-sticky top-0 border-bottom-0 flex-column"
              >
                {/* ایجاد و ساخت تب های فعال در کنار صفحه */}
                {tabCreator(searchParams)?.map((tab, index) => (
                  <div key={index}>
                    {typeof tab === 'string' ? (
                      <HtmlParser html={tab} />
                    ) : (
                      <Nav.Item key={tab.tab}>
                        <Nav.Link
                          eventKey={tab.tab}
                          style={{ borderRight: '2px solid transparent' }}
                          className="cursor-pointer navLinkForceColor outline-none border-bottom-0 rounded-0 fs--1 d-flex justify-content-between"
                        >
                          <div>{tab.title}</div>
                          {/* عدد تعداد جزییات */}
                          {!isFetching ? (
                            <div>
                              {countSuffix(
                                data?.data?.[
                                  tab.tab === 'assets_all' ||
                                  tab.tab === 'links'
                                    ? 'inlink'
                                    : tab.tab
                                  // tab.tab === 'assets_all'
                                  //   ? 'inlink'
                                  //   : tab.tab === 'links'
                                  //   &&
                                  //       (searchParams.get('type') ===
                                  //         'internal' ||
                                  //         searchParams.get('details') ===
                                  //           'internal' )
                                  //     ? 'inlink'
                                  //     : tab.tab
                                ]
                              )}
                            </div>
                          ) : (
                            <div>
                              <Spinner style={{ width: 15, height: 15 }} />
                            </div>
                          )}
                        </Nav.Link>
                      </Nav.Item>
                    )}
                  </div>
                ))}
              </Nav>

              <style>{`.nav-link.active{
                border-right: 2px solid ${getColor('navbar-vertical-default-link-active-color')};
                border-bottom:0 !important;
                background-color:${tabColorFunc(true)} !important;
              }`}</style>
            </Col>
            <Col xs={12} md={10}>
              <Tab.Content>
                {/* ساخت جزییات هر تب بر اساس تب فعال */}
                {[
                  (detailTabsConfig as unknown as DetailTabsMap)[
                    searchParams.get('activeTab') || ''
                  ]
                ]?.map((tab: DetailTabConfig | undefined, index: number) => (
                    <Tab.Pane eventKey={tab?.tab} key={index}>
                      {tab?.cellRenderer}
                    </Tab.Pane>
                  ))}
              </Tab.Content>
            </Col>
          </Row>
        </Tab.Container>
      </Offcanvas.Body>
      <style>
        {/* استایل کلی دراور */}
        {`
        .offcanvas{
         width:1300px !important;
          max-width:100% !important;
        }
      
     `}
      </style>
    </Offcanvas>
  );
};

// =============================================== کامپوننت لینک برای نمایش لینک ها در بالای صفحه
type UrlProps = {
  url?: string;
};

const Url = ({ url }: UrlProps) => {
  const [copied, setCopied] = useState(false);
  const urlFormater = () => {
    if (url?.startsWith('https://'))
      return (
        <>
          <FontAwesomeIcon
            size="sm"
            icon={'lock'}
            title="https"
            color={getColor('success')}
          />
          {url.replace('https://', '')}
        </>
      );
    else if (url?.startsWith('http://'))
      return (
        <>
          <FontAwesomeIcon
            size="sm"
            icon={'lock-open'}
            title="http"
            color={getColor('danger')}
          />
          {url.replace('http://', '')}
        </>
      );
    else {
      return <div></div>;
    }
  };

  return (
    <div
      className="text-end d-flex gap-1 align-items-center"
      style={{ direction: 'ltr' }}
    >
      {url && (
        <>
          <div className="d-flex fs-0 gap-1 align-items-baseline">
            {urlFormater()}
          </div>
          <div>
            {copied ? (
              <MdCheck size={18} />
            ) : (
              <MdContentCopy
                title="کپی پیوند"
                onClick={() => {
                  navigator.clipboard.writeText(url).then(() => {
                    setCopied(true);
                    setTimeout(() => {
                      setCopied(false);
                    }, 3000);
                  });
                }}
                className="cursor-pointer little-icon"
                style={{ paddingLeft: '3px' }}
                size={18}
              />
            )}
            <MdOutlineOpenInNew
              title="بازکردن پیوند"
              onClick={() => window.open(url, '_blank')}
              className="cursor-pointer little-icon"
              style={{ paddingLeft: '3px' }}
              size={18}
            />
          </div>
        </>
      )}

      <style>
        {`
          .table {
            width: auto !important;
            font-size: 14px;
          }

          .table > :not(caption) > * > *{
            color:var(--falcon-gray-800) !important;
          }


          .accordion-button:not(.collapsed),.accordion-button{
            color:var(--falcon-gray-900) !important;
            font-weight: 500;
          }

          thead, tbody, tfoot, tr, td, th {
            border-style: hidden;
          }

          ul{
            list-style-type: auto;
          }
          
          .table tfoot > tr > th:last-child, .table thead > tr > th:last-child, .table tr th:last-child, .table tr td:last-child {
            padding-right: 1.25rem;
          }
        `}
      </style>
    </div>
  );
};

export default PageDrawer;
